/**
 * 共用工具函數
 */

// 圖表顏色
export const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

/**
 * 格式化貨幣金額
 * @param {number} amount - 金額
 * @param {string} currency - 貨幣代碼（預設 TWD）
 * @returns {string} 格式化後的金額字串
 */
export const formatCurrency = (amount, currency = 'TWD') => {
  if (amount === null || amount === undefined) return '$0.00';
  
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(num)) return '$0.00';
  
  // 根據貨幣類型選擇小數位數
  const decimals = currency === 'TWD' ? 0 : 2;
  
  return new Intl.NumberFormat('zh-TW', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num);
};

/**
 * 格式化數字（帶千分位）
 * @param {number} num - 數字
 * @param {number} decimals - 小數位數
 * @returns {string} 格式化後的數字字串
 */
export const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined) return '0';
  
  const value = typeof num === 'string' ? parseFloat(num) : num;
  
  if (isNaN(value)) return '0';
  
  return new Intl.NumberFormat('zh-TW', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
};

/**
 * 格式化百分比
 * @param {number} value - 數值（0-100）
 * @param {number} decimals - 小數位數
 * @returns {string} 格式化後的百分比字串
 */
export const formatPercent = (value, decimals = 1) => {
  if (value === null || value === undefined) return '0%';
  
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(num)) return '0%';
  
  return `${num.toFixed(decimals)}%`;
};

/**
 * 格式化日期
 * @param {string|Date} date - 日期
 * @param {string} format - 格式（'date', 'datetime', 'time'）
 * @returns {string} 格式化後的日期字串
 */
export const formatDate = (date, format = 'date') => {
  if (!date) return '-';
  
  const d = new Date(date);
  
  if (isNaN(d.getTime())) return '-';
  
  const options = {
    date: { year: 'numeric', month: '2-digit', day: '2-digit' },
    datetime: { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' },
    time: { hour: '2-digit', minute: '2-digit' }
  };
  
  return d.toLocaleString('zh-TW', options[format] || options.date);
};

/**
 * 截斷文字
 * @param {string} text - 文字
 * @param {number} maxLength - 最大長度
 * @returns {string} 截斷後的文字
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

/**
 * 延遲函數
 * @param {number} ms - 毫秒數
 * @returns {Promise<void>}
 */
export const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * 防抖函數
 * @param {Function} fn - 要執行的函數
 * @param {number} wait - 等待時間（毫秒）
 * @returns {Function} 防抖後的函數
 */
export const debounce = (fn, wait = 300) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      fn(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};
